import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Scale, ArrowLeft, Calendar, MapPin, Building2, User, 
  Settings, CheckCircle, AlertTriangle, Clock, Wifi, 
  Bluetooth, Radio, Zap, QrCode, Printer
} from 'lucide-react';
import { apiService } from '../services/api';

interface SmartScale {
  id: number;
  scale_name: string;
  brand: string;
  model: string;
  serial_number: string;
  mac_address: string;
  ip_address: string;
  max_capacity_kg: number;
  precision_grams: number;
  location_id: number;
  company_id: number;
  installation_date: string;
  calibration_date: string;
  next_calibration: string;
  status: 'active' | 'inactive' | 'maintenance' | 'calibration_needed';
  communication_protocol: 'wifi' | 'bluetooth' | 'cellular' | 'ethernet';
  assigned_admin_id: number;
  purchase_price: number;
  currency: 'TRY' | 'USD' | 'EUR';
  notes: string;
  location?: {
    name: string;
    address: string;
  };
  company?: {
    name: string;
  };
  assigned_admin?: {
    name: string;
  };
}

interface CalibrationRecord {
  id: number;
  calibration_date: string;
  calibration_type: string;
  calibration_company: string;
  calibration_number: string;
  next_calibration_date: string;
  status: string;
  notes: string;
  calibrated_by_name: string;
}

const ScaleDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [scale, setScale] = useState<SmartScale | null>(null);
  const [calibrationHistory, setCalibrationHistory] = useState<CalibrationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadScaleDetails();
    }
  }, [id]);

  const loadScaleDetails = async () => {
    try {
      setLoading(true);
      const [scaleData, calibrationData] = await Promise.all([
        apiService.getPublicSmartScale(parseInt(id!)),
        apiService.getPublicScaleCalibrationHistory(parseInt(id!))
      ]);

      setScale(scaleData);
      setCalibrationHistory(calibrationData);
    } catch (error: any) {
      console.error('Error loading scale details:', error);
      setError('Terazi bilgileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'calibration_needed': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Aktif';
      case 'inactive': return 'Pasif';
      case 'maintenance': return 'Bakımda';
      case 'calibration_needed': return 'Kalibrasyon Gerekli';
      default: return status;
    }
  };

  const getProtocolIcon = (protocol: string) => {
    switch (protocol) {
      case 'wifi': return Wifi;
      case 'bluetooth': return Bluetooth;
      case 'cellular': return Radio;
      case 'ethernet': return Zap;
      default: return Wifi;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const isCalibrationDue = (nextCalibration: string) => {
    if (!nextCalibration) return false;
    const dueDate = new Date(nextCalibration);
    const today = new Date();
    const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilDue <= 30;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-700 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Terazi bilgileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error || !scale) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-700 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Terazi Bulunamadı</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error || 'Bu terazi bulunamadı veya erişim yetkiniz yok.'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Ana Sayfaya Dön
          </button>
        </div>
      </div>
    );
  }

  const ProtocolIcon = getProtocolIcon(scale.communication_protocol);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-700">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                  <Scale className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{scale.scale_name}</h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{scale.brand} {scale.model}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(scale.status)}`}>
                {getStatusLabel(scale.status)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sol Kolon - Terazi Bilgileri */}
          <div className="lg:col-span-2 space-y-6">
            {/* Temel Bilgiler */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Terazi Bilgileri</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Temel Bilgiler</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Terazi Adı:</span>
                        <span className="font-medium">{scale.scale_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Marka/Model:</span>
                        <span className="font-medium">{scale.brand} {scale.model}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Seri Numarası:</span>
                        <span className="font-medium">{scale.serial_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">MAC Adresi:</span>
                        <span className="font-medium font-mono text-sm">{scale.mac_address}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Teknik Özellikler</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Kapasite:</span>
                        <span className="font-medium">{scale.max_capacity_kg} kg</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Hassasiyet:</span>
                        <span className="font-medium">±{scale.precision_grams}g</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Protokol:</span>
                        <div className="flex items-center">
                          <ProtocolIcon className="w-4 h-4 mr-1" />
                          <span className="font-medium capitalize">{scale.communication_protocol}</span>
                        </div>
                      </div>
                      {scale.ip_address && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">IP Adresi:</span>
                          <span className="font-medium font-mono text-sm">{scale.ip_address}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Lokasyon ve Atama */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Lokasyon ve Atama</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center">
                    <Building2 className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Şirket</p>
                      <p className="font-medium">{scale.company?.name || 'Belirtilmemiş'}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Lokasyon</p>
                      <p className="font-medium">{scale.location?.name || 'Belirtilmemiş'}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <User className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Sorumlu Yetkili</p>
                      <p className="font-medium">{scale.assigned_admin?.name || 'Atanmamış'}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Kurulum Tarihi</p>
                      <p className="font-medium">{formatDate(scale.installation_date)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notlar */}
            {scale.notes && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Notlar</h2>
                </div>
                <div className="p-6">
                  <p className="text-gray-700 dark:text-gray-300">{scale.notes}</p>
                </div>
              </div>
            )}
          </div>

          {/* Sağ Kolon - Kalibrasyon Bilgileri */}
          <div className="space-y-6">
            {/* Kalibrasyon Durumu */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Kalibrasyon Durumu</h2>
              </div>
              <div className="p-6 space-y-4">
                {scale.calibration_date && (
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Son Kalibrasyon</p>
                      <p className="font-medium">{formatDate(scale.calibration_date)}</p>
                    </div>
                  </div>
                )}
                
                {scale.next_calibration && (
                  <div className="flex items-center">
                    {isCalibrationDue(scale.next_calibration) ? (
                      <AlertTriangle className="w-5 h-5 text-orange-500 mr-3" />
                    ) : (
                      <Clock className="w-5 h-5 text-primary mr-3" />
                    )}
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Sonraki Kalibrasyon</p>
                      <p className={`font-medium ${isCalibrationDue(scale.next_calibration) ? 'text-orange-600' : ''}`}>
                        {formatDate(scale.next_calibration)}
                      </p>
                    </div>
                  </div>
                )}

                {isCalibrationDue(scale.next_calibration || '') && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <AlertTriangle className="w-5 h-5 text-orange-500 mr-2" />
                      <p className="text-sm text-orange-800 font-medium">
                        Kalibrasyon yaklaşıyor veya gecikmiş!
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Kalibrasyon Geçmişi */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Kalibrasyon Geçmişi</h2>
              </div>
              <div className="p-6">
                {calibrationHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Settings className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>Henüz kalibrasyon kaydı bulunmuyor</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {calibrationHistory.slice(0, 5).map((record) => (
                      <div key={record.id} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {formatDate(record.calibration_date)}
                          </div>
                          <div className={`text-xs px-2 py-1 rounded-full ${
                            record.status === 'completed' ? 'bg-green-100 text-green-800' :
                            record.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                          }`}>
                            {record.status === 'completed' ? 'Tamamlandı' :
                             record.status === 'pending' ? 'Beklemede' : record.status}
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <p><strong>Tür:</strong> {record.calibration_type === 'manual' ? 'Manuel' :
                                                     record.calibration_type === 'automatic' ? 'Otomatik' :
                                                     record.calibration_type === 'scheduled' ? 'Planlanmış' : record.calibration_type}</p>
                          {record.calibrated_by_name && <p><strong>Kalibre Eden:</strong> {record.calibrated_by_name}</p>}
                          {record.notes && <p><strong>Not:</strong> {record.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* QR Kod Bilgisi */}
            <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <QrCode className="w-5 h-5 text-primary mr-2" />
                <h3 className="text-sm font-medium text-primary-dark">QR Kod Bilgisi</h3>
              </div>
              <p className="text-xs text-primary-dark">
                Bu QR kod okutularak terazi bilgilerine ve kalibrasyon geçmişine erişebilirsiniz.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScaleDetailsPage;
